import React, { useEffect, useState } from 'react';
import { incidentsApi, Incident } from '../../services/incidentService';
import { tasksService, Task as ApiTask } from '../../services/tasksService';
import { capaService, CapaIssue } from '../../services/capaService';
import { userService } from '../../services/userService';
import { usePermissions } from '../../contexts/PermissionContext';
import TriageWorkbench from '../common/triage/TriageWorkbench';
import { TriageSubject } from '../common/triage/types';

// Statuses where the spawned task has reached a state the admin may
// reasonably consider 'done' — i.e. it is safe to manually close the
// originating incident from the triage popup. ARCHIVED already triggers
// the auto-archive hook in tsk_routes.action_close, so this UI is mostly
// for the DONE / VERIFIED cases where no validator is set or no validator
// has acted yet.
const TASK_CLOSEABLE_STATUSES = new Set(['DONE', 'VERIFIED', 'ARCHIVED']);
const TASK_TRIAGE_STATUSES = new Set(['Task Assigned', 'In Progress']);
const CAPA_TRIAGE_STATUSES = new Set(['CAPA Assigned']);

interface IncidentTriageModalProps {
    isOpen: boolean;
    incident: Incident | null;
    onClose: () => void;
    onUpdated: () => void;
}

const IncidentTriageModal: React.FC<IncidentTriageModalProps> = ({ isOpen, incident, onClose, onUpdated }) => {
    const [ownerOptions, setOwnerOptions] = useState<{ value: number; label: string }[]>([]);

    useEffect(() => {
        if (!isOpen) return;
        userService.getUsersDropdown()
            .then(users => setOwnerOptions(users.map(u => ({ value: u.id, label: u.name }))))
            .catch(() => setOwnerOptions([]));
    }, [isOpen]);

    // Preserve the last valid incident so modal body stays populated during the close animation.
    const lastIncidentRef = React.useRef<Incident | null>(incident);
    if (incident) lastIncidentRef.current = incident;
    const activeIncident = lastIncidentRef.current;
    const { check } = usePermissions();
    const canTriage = check('IRG_INCIDENT_TRIAGE', 'view+modify').allowed;

    // Linked-task lookup — only relevant when this incident was previously
    // routed through 'Assign Task' and therefore has a prm_tasks row whose
    // source = (INCIDENT, this.id).
    const [linkedTask, setLinkedTask] = useState<ApiTask | null>(null);
    const [linkedTaskLoading, setLinkedTaskLoading] = useState(false);

    // Linked CAPA issue lookup — when this incident was previously
    // escalated, there's a cap_issues row with
    // issue_source_type='Incident Reporting' and source_record_id=this.id.
    const [linkedIssue, setLinkedIssue] = useState<CapaIssue | null>(null);
    const [linkedIssueLoading, setLinkedIssueLoading] = useState(false);

    const isTaskStatus = !!activeIncident && TASK_TRIAGE_STATUSES.has(activeIncident.remediation_status);
    const isCapaStatus = !!activeIncident && CAPA_TRIAGE_STATUSES.has(activeIncident.remediation_status);

    useEffect(() => {
        if (!isOpen || !activeIncident) { setLinkedTask(null); return; }
        if (!TASK_TRIAGE_STATUSES.has(activeIncident.remediation_status)) {
            setLinkedTask(null);
            return;
        }
        let cancelled = false;
        setLinkedTaskLoading(true);
        tasksService.list({
            source_type_code: 'INCIDENT',
            source_record_id: activeIncident.id,
            limit: 5,
        })
            .then(rows => {
                if (cancelled) return;
                // Most recent first — list endpoint orders by status priority and
                // id DESC, so the first row is the freshest task.
                setLinkedTask(rows[0] || null);
            })
            .catch(() => { if (!cancelled) setLinkedTask(null); })
            .finally(() => { if (!cancelled) setLinkedTaskLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, activeIncident]);

    useEffect(() => {
        if (!isOpen || !activeIncident) { setLinkedIssue(null); return; }
        if (!CAPA_TRIAGE_STATUSES.has(activeIncident.remediation_status)) {
            setLinkedIssue(null);
            return;
        }
        let cancelled = false;
        setLinkedIssueLoading(true);
        capaService.list({ source_type: 'Incident Reporting' })
            .then(rows => {
                if (cancelled) return;
                // Filter to this incident; pick the most recent.
                const matches = rows.filter(r => r.source_record_id === activeIncident.id);
                matches.sort((a, b) => b.id - a.id);
                setLinkedIssue(matches[0] || null);
            })
            .catch(() => { if (!cancelled) setLinkedIssue(null); })
            .finally(() => { if (!cancelled) setLinkedIssueLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, activeIncident]);

    const ownerLabelById = (id: number | null): string => {
        if (id == null) return '';
        return ownerOptions.find(o => o.value === id)?.label ?? '';
    };

    // Build the linked panel payload. We only attach the `task`/`capa` keys
    // when the incident is in the corresponding triage status, so the
    // workbench shows the matching panel (incl. its empty-state) exactly when
    // the original modal did.
    const linked: NonNullable<TriageSubject['linked']> = {};
    if (isTaskStatus) {
        linked.task = linkedTask ? {
            id: linkedTask.id,
            name: linkedTask.name,
            status: linkedTask.status,
            doer_name: linkedTask.doer_name ?? undefined,
            due_date: linkedTask.due_date ?? undefined,
        } : null;
        linked.taskLoading = linkedTaskLoading;
    }
    if (isCapaStatus) {
        linked.capa = linkedIssue ? {
            id: linkedIssue.id,
            issue_code: linkedIssue.issue_code,
            issue_name: linkedIssue.issue_name,
            is_resolved: linkedIssue.is_resolved,
            issue_head_name: linkedIssue.issue_head_name ?? undefined,
            open_action_count: linkedIssue.open_action_count,
            action_count: linkedIssue.action_count,
        } : null;
        linked.capaLoading = linkedIssueLoading;
    }

    const subject: TriageSubject | null = activeIncident ? {
        code: activeIncident.incident_code,
        title: `Triage Incident - ${activeIncident.incident_code}`,
        status: activeIncident.remediation_status,
        canTriage,
        permissionHint: 'You do not have permission to triage incidents. Contact your administrator.',
        panelHeading: 'Incident Report',
        details: [
            { label: 'Occurrence Type', value: activeIncident.occurrence_type || '-' },
            { label: 'Description', value: activeIncident.incident_description },
            { label: 'Reporter', value: activeIncident.reported_by_user_id ? (activeIncident.reported_by_user_name || `User #${activeIncident.reported_by_user_id}`) : 'Anonymous' },
            { label: 'Status', value: activeIncident.remediation_status },
        ],
        linked,
        onTrivial: async p => {
            await incidentsApi.triage(activeIncident.id, {
                decision: 'trivial',
                reporter_feedback: `Reason: ${p.reason}. ${p.feedback}`.trim(),
            });
        },
        onEscalate: async p => {
            await incidentsApi.triage(activeIncident.id, {
                decision: 'escalate',
                risk_severity: p.risk_severity,
                capa_owner: ownerLabelById(p.capa_owner_id),
                capa_owner_id: p.capa_owner_id,
                instructions: p.instructions ?? '',
                target_date: p.target_date,
            });
        },
        onTask: async p => {
            await incidentsApi.triage(activeIncident.id, {
                decision: 'task',
                task_title: p.task_title,
                task_assignee: ownerLabelById(p.task_assignee_id),
                task_assignee_id: p.task_assignee_id,
                task_due_date: p.task_due_date,
                task_description: p.task_description,
                task_grace_period_days: p.task_grace_period_days,
                task_approver_id: p.task_approver_id ?? undefined,
                task_validator_id: p.task_validator_id ?? undefined,
                task_priority: p.task_priority,
            });
        },
        onCloseRecord: (linkedTask && TASK_CLOSEABLE_STATUSES.has(linkedTask.status)) || (linkedIssue && linkedIssue.is_resolved)
            ? async () => { await incidentsApi.archive(activeIncident.id); onUpdated(); }
            : undefined,
    } : null;

    return (
        <TriageWorkbench
            isOpen={isOpen}
            subject={subject}
            ownerOptions={ownerOptions}
            onClose={onClose}
            onUpdated={onUpdated}
        />
    );
};

export default IncidentTriageModal;
